import { z } from "zod";
import { appearanceOverridesSchema } from "./appearance";
import { SDK_VERSION_RE } from "./sdk-version";

/** `embed` param: picks the bridge transport and the session's surface. Absent = hosted page. */
export const EMBED_TRANSPORTS = [
	"flutter",
	"react-native",
	"ios",
	"android",
	"iframe",
	"widget",
] as const;
export type EmbedTransport = (typeof EMBED_TRANSPORTS)[number];

/** A BCP 47 language tag such as `ar` or `en-US`: the page's and the API's locale hint. */
export const localeSchema = z
	.string()
	.regex(/^[A-Za-z]{2,3}(-[A-Za-z0-9]{2,8})*$/);

/** Drops a bad value instead of failing the page: the URL comes from someone else's app. */
const optional = <T extends z.ZodType>(schema: T) =>
	schema.optional().catch(undefined);

/** A retailer's id, which the router reads as a number when it is all digits: taken as text. */
const retailerId = z.preprocess(
	(value) => (typeof value === "number" ? String(value) : value),
	z.string().min(1).max(200),
);

/**
 * Search params of `/c/<orgSlug>` (ADR-5.1), for the route's `validateSearch`.
 * `lt` and `code` are exchanged by the client and then removed from the URL; the rest stay.
 * The appearance overrides (`scheme`, the colours, `corners`, `header`) are params of their own.
 */
export const embedSearchSchema = appearanceOverridesSchema.extend({
	/** Partner-signed launch token (JWT). */
	lt: optional(z.string().min(1).max(4096)),
	/** One-time launch code from POST /v1/sessions. */
	code: optional(z.string().min(1).max(200)),
	embed: optional(z.enum(EMBED_TRANSPORTS)),
	/** Store externalId hint for an anonymous session; ignored with a token or code. */
	store: optional(retailerId),
	/**
	 * The itemId of a product the page opens on (a Meta catalog item's `link` is
	 * `/c/<org>?product=<itemId>`). Read as text like `store`.
	 */
	product: optional(retailerId),
	/** BCP 47 hint for the UI and the first turn. */
	locale: optional(localeSchema),
	/** Iframe/widget only: third candidate for the embedding origin. */
	parentOrigin: optional(z.string().max(300)),
	/** The native SDK's version (X.Y.Z), recorded on the session for the Install screen. */
	sdk: optional(z.string().regex(SDK_VERSION_RE)),
	/**
	 * `debug=1` (an SDK's `debug: true`): the page logs taps, bridge messages and basket
	 * requests to the webview console for the app's developers. Never tokens or message text.
	 */
	debug: optional(z.literal(1)),
});
export type EmbedSearch = z.infer<typeof embedSearchSchema>;
